import {
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';
interface PathData {
  id: number;
  d: string;
  width: number;
  opacity: number;
}

gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin);

@Component({
  selector: 'app-floating-paths',
  templateUrl: './floating-paths.component.html',
  styleUrls: ['./floating-paths.component.scss'],
  standalone: false,
})
export class FloatingPathsComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() position: number = 0; // Default position
  paths: PathData[] = [];
  private tweens: (gsap.core.Tween | gsap.core.Timeline)[] = []; // Can store tweens or timelines
  private animationInitialized = false;
  @ViewChildren('pathElements') pathElements!: QueryList<
    ElementRef<SVGPathElement>
  >;

  ngOnInit(): void {
    this.generatePaths();
  }

  ngAfterViewInit(): void {
    // Ensure paths are generated and elements are available before animating
    if (this.paths.length > 0 && !this.animationInitialized) {
      this.animationInitialized = true;
      if (this.pathElements.length === this.paths.length) {
        this.animatePaths();
      } else {
        // Fallback if elements aren't immediately ready (e.g., *ngFor timing)
        const subscription = this.pathElements.changes.subscribe(() => {
          if (this.pathElements.length === this.paths.length) {
            // Check length again
            this.animatePaths();
            subscription.unsubscribe();
          }
        });
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['position'] && !changes['position'].firstChange) {
      this.generatePaths(); // This will kill existing animations and recreate paths

      // If animations were already set up, re-initialize them for the new paths
      if (this.animationInitialized) {
        // Defer to ensure DOM updates from path changes are applied
        requestAnimationFrame(() => {
          if (this.pathElements.length === this.paths.length) {
            this.animatePaths();
          } else {
            const subscription = this.pathElements.changes.subscribe(() => {
              if (this.pathElements.length === this.paths.length) {
                // Check length again
                this.animatePaths();
                subscription.unsubscribe();
              }
            });
          }
        });
      }
    }
  }

  generatePaths(): void {
    this.killAnimations(); // Kill previous animations before generating new paths
    this.paths = Array.from({ length: 36 }, (_, i) => {
      const p = this.position || 0;

      const d = `M-${380 - i * 5 * p} -${189 + i * 6}C-${380 - i * 5 * p} -${
        189 + i * 6
      } -${312 - i * 5 * p} ${216 - i * 6} ${152 - i * 5 * p} ${343 - i * 6}C${
        616 - i * 5 * p
      } ${470 - i * 6} ${684 - i * 5 * p} ${875 - i * 6} ${684 - i * 5 * p} ${
        875 - i * 6
      }`;

      const width = 0.5 + i * 0.03;
      // Initial opacity for the path element, GSAP will animate style.opacity
      const opacity = 0.1 + i * 0.03;

      return {
        id: i,
        d: d,
        width: width,
        opacity: opacity,
      };
    });
  }

  animatePaths(): void {
    this.killAnimations(); // Clear any existing animations

    if (this.pathElements && this.pathElements.length > 0) {
      this.pathElements.forEach((pathRef: ElementRef<SVGPathElement>) => {
        const pathEl = pathRef.nativeElement;
        const totalLength = pathEl.getTotalLength();

        pathEl.style.strokeDasharray = totalLength.toString();
        pathEl.style.strokeDashoffset = totalLength.toString();
        pathEl.style.opacity = '0';
        const loopDuration = 25 + Math.random() * 10;
        const loopTl = gsap.timeline({ repeat: -1 });

        // From the end of the reveal (strokeDashoffset: 0, opacity: 0.1)
        const revealTween = loopTl
          .to(pathEl, {
            strokeDashoffset: -totalLength,
            opacity: 0.6,
            duration: loopDuration / 2,
            ease: 'none',
          })
          .to(pathEl, {
            strokeDashoffset: 0,
            opacity: 0.3,
            duration: loopDuration / 2,
            ease: 'none',
          });
        this.tweens.push(revealTween); // Add reveal tween for cleanup
      });
    }
  }

  killAnimations(): void {
    this.tweens.forEach((tween) => tween.kill());
    this.tweens = []; // Clear the array
  }

  ngOnDestroy(): void {
    this.killAnimations(); // Kill all GSAP tweens to prevent memory leaks
  }
}
